import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Image as ImageIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  FactCheck as StructuredIcon,
  ShortText as UnstructuredIcon,
  Edit as EditIcon,
  Verified as VerifiedIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { WorkoutDetailDto } from '../../../services/admin-workout.service';
import { getImageUrl } from '../../../utils/image-url.util';

interface WorkoutDetailDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  data: WorkoutDetailDto | null;
  isLoading: boolean;
  getCategoryLabel: (value: string) => string;
  getMeasurementUnitLabel: (value: string) => string;
}

export const WorkoutDetailDialog: React.FC<WorkoutDetailDialogProps> = ({
  open,
  onClose,
  onEdit,
  data,
  isLoading,
  getCategoryLabel,
  getMeasurementUnitLabel: _getMeasurementUnitLabel
}) => {
  if (!data && !isLoading) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Деталі тренування #{data?.id}
        {data?.isSystem && (
          <Tooltip title="Системний елемент">
            <VerifiedIcon color="primary" />
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
             <Typography>Завантаження...</Typography>
          </Box>
        ) : data ? (
          <Grid container spacing={3}>
            {/* Header with Image and Basic Info */}
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                  variant="rounded"
                  src={data.imageUrl ? getImageUrl(data.imageUrl) : undefined}
                  alt="Workout Image"
                  sx={{ width: 150, height: 150, mb: 2 }}
                >
                  <ImageIcon sx={{ fontSize: 60 }} />
                </Avatar>
                
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Chip 
                    icon={data.isStructured ? <StructuredIcon /> : <UnstructuredIcon />} 
                    label={data.isStructured ? "Структурований" : "Неструктурований"} 
                    color={data.isStructured ? "success" : "default"} 
                    variant="outlined"
                  />
                  <Chip 
                    label={getCategoryLabel(data.category)} 
                    color="primary" 
                    size="small" 
                  />
                </Box>

                {data.externalLink && (
                  <Button 
                    startIcon={<LinkIcon />} 
                    href={data.externalLink} 
                    target="_blank" 
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Зовнішнє посилання
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              {/* Translations Table */}
              <Typography variant="h6" gutterBottom>Назви (Переклади)</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Мова</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Назва</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.nameI18n && Object.entries(data.nameI18n).map(([lang, name]) => (
                      <TableRow key={lang}>
                        <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold', width: '60px' }}>{lang}</TableCell>
                        <TableCell>{name || <Typography variant="caption" color="text.secondary">Немає перекладу</Typography>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Details / Description Table */}
              {data.detailsI18n && Object.keys(data.detailsI18n).length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Опис / Інструкція</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Мова</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Опис</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(data.detailsI18n).map(([lang, text]) => (
                          <TableRow key={lang}>
                            <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold', width: '60px', verticalAlign: 'top' }}>{lang}</TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                              {text || <Typography variant="caption" color="text.secondary">Порожньо</Typography>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Measurement Info */}
              <Typography variant="h6" gutterBottom>Параметри</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* <Grid item xs={6} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Одиниця виміру</Typography>
                  <Typography variant="body1">{getMeasurementUnitLabel(data.measurementUnit)}</Typography>
                </Grid> */}
                <Grid item xs={6} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">MET значення</Typography>
                  <Typography variant="body1" fontWeight="bold">{data.metValue ?? '-'}</Typography>
                </Grid>
                 <Grid item xs={6} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">MET Activity ID</Typography>
                  <Typography variant="body1">{data.metActivityId ?? '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Дефолтне значення</Typography>
                  <Typography variant="body1">{data.defaultMeasurementValue ?? '-'}</Typography>
                </Grid>
              </Grid>

              {data.metActivity && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f7ff', borderColor: '#cfe8fc' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <ScienceIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                        {data.metActivity.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {data.metActivity.nameEn}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip 
                          label={`MET: ${data.metActivity.metValue}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={data.metActivity.categoryName} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={data.metActivity.compendiumType} 
                          size="small" 
                          variant="outlined" 
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Grid>

            {/* Footer Info */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                {/* Author Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon /> Автор
                  </Typography>
                  {data.creator ? (
                    <Box>
                      <Typography variant="body1" fontWeight="bold">{data.creator.fullName}</Typography>
                      <Typography variant="body2" color="text.secondary">{data.creator.email}</Typography>
                      <Typography variant="caption" color="text.secondary">ID: {data.creator.id}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" color="text.secondary">Система</Typography>
                      {data.isSystem && <VerifiedIcon color="primary" fontSize="small" />}
                    </Box>
                  )}
                </Grid>

                {/* Statistics */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon /> Статистика
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Використано разів</Typography>
                      <Typography variant="h6" color="primary">{data.usageCount}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Створено</Typography>
                      <Typography variant="body2">{new Date(data.createdAt).toLocaleDateString()}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Оновлено</Typography>
                      <Typography variant="body2">{new Date(data.updatedAt).toLocaleDateString()}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Typography color="error" align="center">Не вдалося завантажити дані</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Закрити</Button>
        <Button onClick={onEdit} startIcon={<EditIcon />} color="primary" variant="contained" autoFocus>
          Редагувати
        </Button>
      </DialogActions>
    </Dialog>
  );
};
